/* ═══════════════════════════════════════════════════════════════════════════
   animations.js  v3  —  schema-driven client-side animation engine

   SCHEMA (optional — define before this file loads):
     window.AnimSchema = {
       profile:  'subtle' | 'standard' | 'energetic',   // default: standard
       rules:    [...],   // extra rules merged after profile rules
       settings: { staggerMs, threshold, rootMargin, scrollBar },
     }

   DATA ATTRIBUTES (per-element overrides):
     data-anim="fade-up|zoom-in|…"    explicit entrance (overrides RULES)
     data-anim="none"                 opt-out this element only
     data-anim-hover="lift|glow|scale|underline"
     data-anim-delay="200"            transition-delay in ms
     data-anim-stagger[="80"]         mark container; optional ms per child
     data-no-anim                     opt-out element + all descendants

   Framework-agnostic — works on Frappe, Bootstrap, plain HTML.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* ── Read schema ──────────────────────────────────────────────────────── */

  var schema   = window.AnimSchema || {};
  var profile  = schema.profile || 'standard';
  var settings = Object.assign({
    staggerMs:   80,
    threshold:   0.1,
    rootMargin:  '0px 0px -40px 0px',
    scrollBar:   true,
  }, schema.settings || {});


  /* ── Entrance / hover class registry ──────────────────────────────────── */

  var ENTRANCE = [
    'anim-fade-up', 'anim-fade-down', 'anim-fade-in',
    'anim-slide-left', 'anim-slide-right',
    'anim-zoom-in', 'anim-flip-up', 'anim-bounce-in',
    'anim-word-reveal',
  ];

  var HOVER_MAP = {
    lift:      'anim-hover-lift',
    glow:      'anim-hover-glow',
    scale:     'anim-hover-scale',
    underline: 'anim-underline',
  };

  var ENTRANCE_SEL = ENTRANCE.map(function (c) { return '.' + c; }).join(',');


  /* ── Profile rule sets ────────────────────────────────────────────────── */
  /*
     Rules are matched top-to-bottom; first entrance / first hover wins.
     Put specific selectors before generic ones.
  */

  /* Explicit class names only — the attribute-substring variants
     ([class*="-card"] etc.) forced a full-document attribute scan per rule
     and swept in elements that were never meant to animate. */
  var CARD_SEL = [
    '.card',
    '.product-card', '.item-card', '.item-group-card',
    '.web-item-card',
    // Frappe website / ERPNext portal patterns — all list portal pages use these
    '.web-list-item',                           // /orders, /addresses, /quotations, etc.
    '.transaction-list-item',                   // ERPNext transaction portal rows
    '.frappe-card', '.page-card',               // frappe generic card + nav cards
    '.blog-card',                               // blog listing pages
    '.search-result',                           // /search results
  ].join(', ');

  var GRID_CHILDREN_SEL = [
    '[class*="-list"] > li',
    'ul.product-list > li',
    '.products-list > .col',
    '.item-group-list > .col',
    '.card-group > [class*="col"]',
    // Frappe portal list container
    '.result > *',
  ].join(', ');

  var PROFILES = {

    /* ── subtle ─────────────────────────────────────────────────────────── */
    /* Calm, low-motion.  Good for corporate / editorial sites.             */
    subtle: [
      { sel: 'section, article, aside',                                     entrance: 'anim-fade-in'  },
      { sel: '[class*="section-header"], [class*="section_header"], .page-header', entrance: 'anim-fade-up'  },
      { sel: 'h1',                                                          entrance: 'anim-fade-up'  },
      { sel: 'h2, h3',                                                      entrance: 'anim-fade-up'  },
      { sel: 'h4, h5, h6',                                                  entrance: 'anim-fade-in'  },
      { sel: 'p.lead, .subtitle, [class*="tagline"], [class*="-subheading"]', entrance: 'anim-fade-in' },
      { sel: CARD_SEL,                                                       entrance: 'anim-fade-up'  },
      { sel: 'blockquote, [class*="testimonial"], [class*="-quote"]',        entrance: 'anim-fade-in'  },
      // rfhq-pc-img--alt (the product card's hover-swap image) manages its own
      // opacity via :hover/:focus-within. .anim-visible{opacity:1!important}
      // would permanently defeat that the first time it scrolls into view —
      // the alt image would stay visible and, stacked in the same grid cell,
      // paint over the primary photo. Skipped in all three profiles below.
      { sel: 'img',  entrance: 'anim-fade-in',  skip: ['avatar','logo','icon','flag','emoji','brand','rfhq-pc-img--alt'] },
      { sel: 'figure',                                                       entrance: 'anim-fade-up'  },
      { sel: 'figcaption',                                                   entrance: 'anim-fade-in'  },
      { sel: GRID_CHILDREN_SEL,                                              entrance: 'anim-fade-up'  },
      // No hover effects in subtle mode
    ],

    /* ── standard ───────────────────────────────────────────────────────── */
    /* Balanced.  Zoom on cards, flip on features, bounce on stats/badges.  */
    standard: [
      { sel: 'section, article, aside',                                       entrance: 'anim-fade-up' },
      { sel: '[class*="section-header"], [class*="section_header"], [class*="hero-"], .page-header', entrance: 'anim-fade-up' },
      { sel: 'h1',                                                             entrance: 'anim-fade-up' },
      { sel: 'h2, h3',                                                         entrance: 'anim-fade-up' },
      { sel: 'h4, h5, h6',                                                     entrance: 'anim-fade-in' },
      { sel: 'p.lead, .subtitle, [class*="tagline"], [class*="-subheading"]',  entrance: 'anim-fade-in' },
      // specific card types first (prevent generic card rule from overriding)
      { sel: '[class*="feature-card"], [class*="service-card"], [class*="icon-box"], [class*="iconbox"]',
        entrance: 'anim-flip-up',   hover: ['anim-hover-lift'] },
      { sel: '[class*="-stat"], [class*="stat-"], [class*="counter-"], [class*="-counter"], [class*="-metric"], [class*="metric-"]',
        entrance: 'anim-bounce-in' },
      { sel: '[class*="-cta"], [class*="cta-"]',
        entrance: 'anim-zoom-in' },
      { sel: '.badge, [class*="-badge"], [class*="-tag"]:not(input):not(label), [class*="tag-"], [class*="-pill"]',
        entrance: 'anim-bounce-in' },
      { sel: 'blockquote, [class*="testimonial"], [class*="-quote"]',          entrance: 'anim-slide-left' },
      { sel: '.sidebar, [class*="-sidebar"], aside.widget, [class*="widget-area"]', entrance: 'anim-slide-right' },
      // generic cards (catches everything not matched above)
      { sel: CARD_SEL, entrance: 'anim-zoom-in', hover: ['anim-hover-lift'] },
      { sel: 'img',    entrance: 'anim-zoom-in', skip: ['avatar','logo','icon','flag','emoji','brand','rfhq-pc-img--alt'] },
      { sel: 'figure',     entrance: 'anim-fade-up'  },
      { sel: 'figcaption', entrance: 'anim-fade-in'  },
      { sel: GRID_CHILDREN_SEL, entrance: 'anim-fade-up' },
      { sel: '.btn, button, [class*="-btn"], [class*="btn-"]',
        hover: ['anim-hover-lift'],  skip: ['submit','close','dismiss','navbar','anim-no-hover'] },
      { sel: '.read-more, [class*="learn-more"], [class*="see-all"], [class*="view-all"]',
        hover: ['anim-underline'] },
    ],

    /* ── energetic ──────────────────────────────────────────────────────── */
    /* High-impact.  Word-reveal on H1, flip/bounce throughout, scale hover. */
    energetic: [
      { sel: 'section, article, aside',                                        entrance: 'anim-fade-up' },
      { sel: '[class*="section-header"], [class*="section_header"], [class*="hero-"], .page-header', entrance: 'anim-fade-up' },
      { sel: 'h1',    entrance: 'anim-word-reveal' },   // dramatic word-by-word
      { sel: 'h2, h3', entrance: 'anim-flip-up'   },
      { sel: 'h4, h5, h6', entrance: 'anim-fade-up' },
      { sel: 'p.lead, .subtitle, [class*="tagline"], [class*="-subheading"]',  entrance: 'anim-slide-left' },
      // specific card types first
      { sel: '[class*="feature-card"], [class*="service-card"], [class*="icon-box"], [class*="iconbox"]',
        entrance: 'anim-flip-up',   hover: ['anim-hover-glow'] },
      { sel: '[class*="-stat"], [class*="stat-"], [class*="counter-"], [class*="-counter"], [class*="-metric"], [class*="metric-"]',
        entrance: 'anim-bounce-in' },
      { sel: '[class*="-cta"], [class*="cta-"]',
        entrance: 'anim-bounce-in', hover: ['anim-hover-scale'] },
      { sel: '.badge, [class*="-badge"], [class*="-tag"]:not(input):not(label), [class*="tag-"], [class*="-pill"]',
        entrance: 'anim-bounce-in' },
      { sel: 'blockquote, [class*="testimonial"], [class*="-quote"]',           entrance: 'anim-slide-left' },
      { sel: '.sidebar, [class*="-sidebar"], aside.widget, [class*="widget-area"]', entrance: 'anim-slide-right' },
      // generic cards
      { sel: CARD_SEL, entrance: 'anim-zoom-in',  hover: ['anim-hover-scale'] },
      { sel: 'img',    entrance: 'anim-zoom-in',  skip: ['avatar','logo','icon','flag','emoji','brand','rfhq-pc-img--alt'] },
      { sel: 'figure',     entrance: 'anim-fade-up' },
      { sel: 'figcaption', entrance: 'anim-fade-in' },
      { sel: GRID_CHILDREN_SEL, entrance: 'anim-zoom-in' },
      { sel: '.btn, button, [class*="-btn"], [class*="btn-"]',
        hover: ['anim-hover-scale'], skip: ['submit','close','dismiss','navbar','anim-no-hover'] },
      { sel: '.read-more, [class*="learn-more"], [class*="see-all"], [class*="view-all"]',
        hover: ['anim-underline'] },
    ],

  };


  /* ── Merge profile + custom rules ─────────────────────────────────────── */

  var RULES = (PROFILES[profile] || PROFILES.standard).concat(schema.rules || []);


  /* ── Stagger container selectors ──────────────────────────────────────── */

  var STAGGER_SEL = [
    '[class*="-grid"]', '[class*="grid-"]',
    '[class*="-list"]',
    'ul.product-list', '.products-list', '.item-group-list',
    '.card-group',
    // Frappe portal list container — staggers .web-list-item children automatically
    '.result',
  ].join(', ');


  /* ── Semantic runners ─────────────────────────────────────────────────── */

  var RUNNERS = [
    { sel: '[data-count]',                          fn: runCountUp  },
    { sel: '[class*="-stars"], [class*="stars-"]',  fn: runStarFill },
  ];


  /* ── Utilities ────────────────────────────────────────────────────────── */

  function qsa(root, sel) {
    try {
      var hits = root.querySelectorAll ? Array.from(root.querySelectorAll(sel)) : [];
      if (root.matches && root.matches(sel)) hits.unshift(root);
      // The device capability library's live screen (.dc-view) animates itself; a class like "dc-status" must not be taken for a stat or badge.
      return hits.filter(function (el) { return !(el.closest && el.closest('.dc-view')); });
    } catch (_) { return []; }
  }

  function hasEntrance(el) {
    return ENTRANCE.some(function (c) { return el.classList.contains(c); });
  }

  function hasHover(el) {
    return Object.values(HOVER_MAP).some(function (c) { return el.classList.contains(c); });
  }

  function shouldSkip(el, words) {
    if (!words || !words.length) return false;
    var cls = el.className || '';
    return words.some(function (w) { return cls.indexOf(w) !== -1; });
  }


  /* ── IntersectionObserver ─────────────────────────────────────────────── */

  var io;

  function reveal(el) {
    if (el.classList.contains('anim-visible')) return;
    el.classList.add('anim-visible');
    fireRunners(el);
    io.unobserve(el);
    if (el._animFallback) { clearTimeout(el._animFallback); el._animFallback = null; }
  }

  function initIO() {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        reveal(entry.target);
      });
    }, { threshold: settings.threshold, rootMargin: settings.rootMargin });
  }

  /* Product grids (and any other content) re-render on filter/sort/page
     changes — every insert goes through rfhqDom -> scan()+attachIO() again,
     same as a first load. An element that's already sitting inside the
     viewport at the moment it's freshly tagged and observed should get its
     initial IO callback almost immediately (that's standard
     IntersectionObserver behaviour on observe()) -- but found live, on a
     fast re-render that first callback can be lost (observed, then
     unobserved/reobserved again before the browser gets to report it, or
     simply never scheduled if nothing about scroll position "changes" from
     the engine's point of view), leaving an already-visible card hidden at
     opacity:0 indefinitely. Only elements ALREADY in (or very near) the
     viewport the instant they're observed get a short safety timer for
     this exact case -- anything genuinely below the fold is left to real
     IO/scroll semantics untouched, so the progressive reveal-as-you-scroll
     effect on long pages still works exactly as before. */
  var FALLBACK_MS = 400;

  function isNearViewport(el) {
    var r = el.getBoundingClientRect();
    var vh = window.innerHeight || document.documentElement.clientHeight;
    return r.bottom > -40 && r.top < vh + 40;
  }

  function attachIO(root) {
    qsa(root, ENTRANCE_SEL).forEach(function (el) {
      if (el.classList.contains('anim-visible')) return;
      io.observe(el);
      if (isNearViewport(el)) {
        el._animFallback = setTimeout(function () { reveal(el); }, FALLBACK_MS);
      }
    });
  }


  /* ── DOM Scanner ──────────────────────────────────────────────────────── */

  /* Hard ceiling on rule-driven entrance tagging. Every tagged element is
     hidden until its IntersectionObserver entry fires, and each carries a
     transition the compositor must track — on very long pages tagging
     everything costs more in jank than the reveal is worth. Elements past
     the cap simply render visible. Explicit data-anim requests are exempt. */
  var MAX_ENTRANCE_TAGS = 250;
  var entranceTagCount  = 0;

  function scan(root) {

    /* 1 ── Apply profile RULES (first entrance / first hover wins) ──────── */
    RULES.forEach(function (rule) {
      qsa(root, rule.sel).forEach(function (el) {
        if (el.closest('[data-no-anim]')) return;
        if (el.dataset.anim === 'none') return;
        if (shouldSkip(el, rule.skip)) return;
        if (rule.entrance && !hasEntrance(el) && entranceTagCount < MAX_ENTRANCE_TAGS) {
          el.classList.add(rule.entrance);
          entranceTagCount++;
        }
        if (rule.hover    && !hasHover(el))    rule.hover.forEach(function (c) { el.classList.add(c); });
      });
    });

    /* 2 ── data-anim attribute overrides (highest priority) ────────────── */
    qsa(root, '[data-anim]').forEach(function (el) {
      if (el.closest('[data-no-anim]')) return;
      var val = el.dataset.anim;
      if (!val || val === 'none') return;
      var cls = val.indexOf('anim-') === 0 ? val : 'anim-' + val;
      if (ENTRANCE.indexOf(cls) !== -1) {
        ENTRANCE.forEach(function (c) { el.classList.remove(c); });  // clear rule-set class
        el.classList.add(cls);
      }
    });

    qsa(root, '[data-anim-hover]').forEach(function (el) {
      if (el.closest('[data-no-anim]')) return;
      var key = el.dataset.animHover;
      el.classList.add(HOVER_MAP[key] || ('anim-hover-' + key));
    });

    qsa(root, '[data-anim-delay]').forEach(function (el) {
      if (el.closest('[data-no-anim]')) return;
      el.style.transitionDelay = (parseInt(el.dataset.animDelay, 10) || 0) + 'ms';
    });

    /* 3 ── Pre-split word-reveal elements so spans exist before IO fires ── */
    qsa(root, '.anim-word-reveal, [data-word-reveal]').forEach(function (el) {
      if (el._wordSplit || el.closest('[data-no-anim]')) return;
      el._wordSplit = true;
      el.classList.add('anim-word-reveal');
      /* Preserve the full text as aria-label BEFORE splitting so screen
         readers announce the complete heading, not each word-span in turn. */
      var fullText = el.textContent.trim();
      if (!el.getAttribute('aria-label')) el.setAttribute('aria-label', fullText);
      el.innerHTML = fullText.split(/\s+/).map(function (w, i) {
        return '<span class="anim-word" aria-hidden="true" style="--wi:' + i + '">' + w + '</span>';
      }).join(' ');
    });

    /* 4 ── Stagger: set --si + --anim-stagger-ms on grid/list containers ── */
    qsa(root, STAGGER_SEL).forEach(function (c) { applyStagger(c, settings.staggerMs); });

    qsa(root, '[data-anim-stagger]').forEach(function (c) {
      var ms = parseInt(c.dataset.animStagger, 10) || settings.staggerMs;
      applyStagger(c, ms);
    });
  }

  function applyStagger(container, ms) {
    if (container.closest('[data-no-anim]')) return;
    container.classList.add('anim-stagger');
    container.style.setProperty('--anim-stagger-ms', ms + 'ms');
    Array.from(container.children).forEach(function (child, i) {
      if (!child.closest('[data-no-anim]')) child.style.setProperty('--si', i);
    });
  }


  /* ── Semantic Runners ─────────────────────────────────────────────────── */

  function fireRunners(el) {
    RUNNERS.forEach(function (r) {
      try {
        if (el.matches && el.matches(r.sel)) r.fn(el);
        if (el.querySelectorAll) el.querySelectorAll(r.sel).forEach(r.fn);
      } catch (_) {}
    });
  }

  function runCountUp(el) {
    if (el._animDone) return;
    el._animDone = true;
    var raw    = (el.dataset.count || el.textContent).trim();
    var num    = parseFloat(raw.replace(/[^0-9.]/g, ''));
    if (isNaN(num) || num === 0) return;
    var prefix = raw.match(/^[^0-9]*/)[0];
    var suffix = raw.replace(/^[^0-9]*/, '').replace(/^[0-9.]+/, '');
    var dp     = ((raw.match(/\.(\d+)/) || ['',''])[1] || '').length;
    var dur = settings.countUpMs || 900, t0 = null;
    (function step(ts) {
      if (!t0) t0 = ts;
      var p = Math.min((ts - t0) / dur, 1);
      el.textContent = prefix + ((1 - Math.pow(1 - p, 3)) * num).toFixed(dp) + suffix;
      if (p < 1) requestAnimationFrame(step);
    }(performance.now()));
  }

  function runStarFill(container) {
    if (container._animDone) return;
    container._animDone = true;
    var filled = container.querySelectorAll(
      '[class*="--filled"], [class*="-active"], [class*="filled"], [class*="checked"]'
    );
    Array.from(filled).forEach(function (star, i) {
      setTimeout(function () { star.classList.add('anim-star-lit'); }, i * 65);
    });
  }


  /* ── Scroll Progress Bar ──────────────────────────────────────────────── */
  /*
     scrollHeight forces synchronous layout, so it must never be read inside
     the scroll handler itself — that was measurable jank on every scroll
     event. The max is cached and refreshed only when the page can actually
     change height (resize, load, DOM insertions), and the style write is
     coalesced to one per frame.
  */

  var scrollMax = 0;

  function invalidateScrollMax() {
    scrollMax = document.body.scrollHeight - window.innerHeight;
  }

  function initScrollBar() {
    if (document.getElementById('anim-scroll-bar')) return;
    var bar = document.createElement('div');
    bar.id  = 'anim-scroll-bar';
    document.body.prepend(bar);

    invalidateScrollMax();
    window.addEventListener('load', invalidateScrollMax);
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(invalidateScrollMax, 150);
    });

    var pendingFrame = null;
    window.addEventListener('scroll', function () {
      if (pendingFrame !== null) return;
      pendingFrame = requestAnimationFrame(function () {
        pendingFrame = null;
        bar.style.width =
          (scrollMax > 0 ? Math.min(window.scrollY / scrollMax * 100, 100) : 0) + '%';
      });
    }, { passive: true });
  }


  /* ── DOM insertions after load ────────────────────────────────────────── */
  /* Requires window.rfhqDom (rfhq_dom_observer.js — always the first import
     of websitehq.bundle.js). Never add a private MutationObserver here. */

  function initMO() {
    window.rfhqDom.onAdded(function (roots) {
      roots.forEach(function (node) {
        scan(node);
        attachIO(node);
      });
      invalidateScrollMax();  // injected grids change the page height
    });
  }


  /* ── Boot ─────────────────────────────────────────────────────────────── */

  /* Pages where animation is semantically wrong — auth flows, error pages */
  var NO_ANIM_PATHS = [
    '/login', '/register', '/update-password', '/complete-signup',
    '/forgot', '/verify', '/qrcode', '/printview', '/printpreview',
  ];

  function boot() {
    var path = window.location.pathname.replace(/\/$/, '');
    if (NO_ANIM_PATHS.indexOf(path) !== -1) return;
    if (document.body && document.body.hasAttribute('data-no-anim')) return;
    /* Skip entrance animations entirely when OS reduce-motion is on.
       CSS already makes elements visible; skipping anim-ready prevents the
       brief hide→show flash and keeps word-reveal spans from staying invisible. */
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.documentElement.classList.add('anim-ready');
    initIO();
    scan(document);

    /* Raftor PWA HQ shows a full-screen splash (data-pwahq="loading" on
       <html>) while it builds the mobile shell. IntersectionObserver
       doesn't care about z-index, so observing now would resolve entrance
       animations to their final state underneath the splash and the
       reveal would never be seen. Wait for the splash to clear first. */
    if (document.documentElement.hasAttribute('data-pwahq')) {
      var splashMO = new MutationObserver(function () {
        if (!document.documentElement.hasAttribute('data-pwahq')) {
          splashMO.disconnect();
          attachIO(document);
        }
      });
      splashMO.observe(document.documentElement, { attributes: true, attributeFilter: ['data-pwahq'] });
    } else {
      attachIO(document);
    }

    initMO();
    if (settings.scrollBar && !document.body.hasAttribute('data-no-scroll-bar')) {
      initScrollBar();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

}());
